import { useState, useMemo } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { formatBCH } from '@/lib/bch';
import { cn } from '@/lib/utils';
import {
  loadCashStamps,
  upsertCashStamp,
  deleteCashStamp,
  isStampActive,
  remainingClaims,
  remainingBudget,
  totalDistributed,
  getCashStampStats,
  type CashStamp,
} from '@/lib/cashStampStorage';

// ─── Create / Edit Form ──────────────────────────────────────────────────────

function CreateStampForm({
  initial,
  merchantName,
  onSave,
  onCancel,
}: {
  initial?: Partial<CashStamp>;
  merchantName: string;
  onSave: (stamp: CashStamp) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rewardStr, setRewardStr] = useState(initial?.rewardBCH?.toString() ?? '');
  const [budgetStr, setBudgetStr] = useState(initial?.totalBudgetBCH?.toString() ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initial?.websiteUrl ?? '');
  const [bchLocalUrl, setBchLocalUrl] = useState(initial?.bchLocalUrl ?? '');

  // Default expiry: 30 days from now
  const defaultExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const [expiryDate, setExpiryDate] = useState(
    initial?.expiresAt
      ? new Date(initial.expiresAt).toISOString().slice(0, 10)
      : defaultExpiry.toISOString().slice(0, 10)
  );

  const reward = parseFloat(rewardStr) || 0;
  const budget = parseFloat(budgetStr) || 0;
  const maxClaims = reward > 0 ? Math.floor(budget / reward) : 0;
  const effectiveBudget = maxClaims * reward;

  const handleSave = () => {
    if (!title.trim()) return alert('Enter a title for your CashStamp.');
    if (reward <= 0) return alert('Enter a BCH reward amount per claim.');
    if (budget <= 0) return alert('Enter a total BCH budget.');
    if (maxClaims <= 0) return alert('Budget must cover at least one claim.');
    if (!expiryDate) return alert('Set an expiration date.');

    const expiresAt = new Date(expiryDate + 'T23:59:59').getTime();
    if (expiresAt <= Date.now()) return alert('Expiration date must be in the future.');

    const stamp: CashStamp = {
      id: initial?.id ?? crypto.randomUUID(),
      merchantName: merchantName || 'BCH Merchant',
      title: title.trim(),
      description: description.trim(),
      rewardBCH: reward,
      totalBudgetBCH: effectiveBudget,
      maxClaims,
      fundingAddress: '',
      createdAt: initial?.createdAt ?? Date.now(),
      expiresAt,
      websiteUrl: websiteUrl.trim() || undefined,
      bchLocalUrl: bchLocalUrl.trim() || undefined,
      disabled: false,
      claims: initial?.claims ?? [],
      views: initial?.views ?? 0,
    };

    onSave(stamp);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe space-y-4 max-h-[85dvh] overflow-y-auto">
        <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto" />
        <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50">
          {initial?.id ? 'Edit CashStamp' : 'Create CashStamp'}
        </h3>
        <p className="text-xs text-zinc-400 leading-relaxed">
          Create a promotional BCH giveaway. Customers claim a fixed BCH reward — great for driving traffic and onboarding new customers.
        </p>

        {/* Title */}
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Promotion Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Grand Opening Special"
            maxLength={60}
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Visit our new location and claim free BCH!"
            maxLength={200}
            rows={2}
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E] resize-none"
          />
        </div>

        {/* Reward + Budget side by side */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
              Reward per claim (BCH)
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.0001"
              value={rewardStr}
              onChange={(e) => setRewardStr(e.target.value)}
              placeholder="0.001"
              className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm font-mono text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
              Total Budget (BCH)
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.001"
              value={budgetStr}
              onChange={(e) => setBudgetStr(e.target.value)}
              placeholder="0.1"
              className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm font-mono text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
            />
          </div>
        </div>

        {/* Auto-calculated stats */}
        {reward > 0 && budget > 0 && (
          <div className="flex gap-2">
            <div className="flex-1 bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-3 py-2.5 border border-[#0AC18E]/20 text-center">
              <p className="text-lg font-black text-[#0AC18E]">{maxClaims}</p>
              <p className="text-[10px] text-zinc-500 font-semibold uppercase">Max Claims</p>
            </div>
            <div className="flex-1 bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-3 py-2.5 border border-[#0AC18E]/20 text-center">
              <p className="text-lg font-black text-[#0AC18E]">{formatBCH(effectiveBudget)}</p>
              <p className="text-[10px] text-zinc-500 font-semibold uppercase">Effective Budget</p>
            </div>
          </div>
        )}

        {/* Expiration */}
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Expiration Date
          </label>
          <input
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        {/* Optional URLs */}
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Website URL <span className="font-normal normal-case text-zinc-300">(optional)</span>
          </label>
          <input
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://myshop.com"
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            BCH Local Listing <span className="font-normal normal-case text-zinc-300">(optional)</span>
          </label>
          <input
            type="url"
            value={bchLocalUrl}
            onChange={(e) => setBchLocalUrl(e.target.value)}
            placeholder="https://bchlocal.com/merchant/..."
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancel}
            className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-[2] py-4 rounded-2xl text-sm font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
          >
            {initial?.id ? 'Save Changes' : '🎁 Create CashStamp'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Stamp Card ──────────────────────────────────────────────────────────────

function StampCard({
  stamp,
  onToggle,
  onShare,
  onDelete,
}: {
  stamp: CashStamp;
  onToggle: () => void;
  onShare: () => void;
  onDelete: () => void;
}) {
  const active = isStampActive(stamp);
  const remaining = remainingClaims(stamp);
  const distributed = totalDistributed(stamp);
  const budget = remainingBudget(stamp);
  const claimRate = stamp.views > 0 ? ((stamp.claims.length / stamp.views) * 100).toFixed(1) : '0';
  const isExpired = Date.now() > stamp.expiresAt;
  const isFull = stamp.claims.length >= stamp.maxClaims;
  const [showDelete, setShowDelete] = useState(false);

  const statusLabel = stamp.disabled
    ? 'Disabled'
    : isExpired
    ? 'Expired'
    : isFull
    ? 'Fully Claimed'
    : 'Active';

  const statusColor = active
    ? 'bg-[#0AC18E]/10 text-[#0AC18E] border-[#0AC18E]/20'
    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 border-zinc-200 dark:border-zinc-700';

  return (
    <div className={cn(
      'bg-white dark:bg-zinc-800 rounded-2xl border overflow-hidden transition-all',
      active ? 'border-[#0AC18E]/30' : 'border-zinc-200 dark:border-zinc-700 opacity-75'
    )}>
      {/* Header */}
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0 text-xl">
          🎁
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-zinc-900 dark:text-zinc-50 truncate">{stamp.title}</p>
          {stamp.description && (
            <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{stamp.description}</p>
          )}
        </div>
        <span className={cn('text-[10px] font-bold px-2 py-1 rounded-full border shrink-0', statusColor)}>
          {statusLabel}
        </span>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-px bg-zinc-100 dark:bg-zinc-700 border-t border-zinc-100 dark:border-zinc-700">
        <div className="bg-white dark:bg-zinc-800 px-3 py-2.5 text-center">
          <p className="text-sm font-black text-zinc-900 dark:text-zinc-50">{stamp.claims.length}</p>
          <p className="text-[9px] text-zinc-400 font-semibold uppercase">Claims</p>
        </div>
        <div className="bg-white dark:bg-zinc-800 px-3 py-2.5 text-center">
          <p className="text-sm font-black text-zinc-900 dark:text-zinc-50">{remaining}</p>
          <p className="text-[9px] text-zinc-400 font-semibold uppercase">Left</p>
        </div>
        <div className="bg-white dark:bg-zinc-800 px-3 py-2.5 text-center">
          <p className="text-sm font-black text-[#0AC18E]">{formatBCH(distributed)}</p>
          <p className="text-[9px] text-zinc-400 font-semibold uppercase">Sent</p>
        </div>
        <div className="bg-white dark:bg-zinc-800 px-3 py-2.5 text-center">
          <p className="text-sm font-black text-zinc-900 dark:text-zinc-50">{stamp.views}</p>
          <p className="text-[9px] text-zinc-400 font-semibold uppercase">Views</p>
        </div>
      </div>

      {/* Footer details + actions */}
      <div className="px-4 py-3 flex items-center gap-2 border-t border-zinc-100 dark:border-zinc-700">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-zinc-400">
            <span className="font-semibold">{formatBCH(stamp.rewardBCH)} BCH</span> per claim
            &nbsp;·&nbsp;{formatBCH(budget)} BCH left
            &nbsp;·&nbsp;{claimRate}% rate
          </p>
          <p className="text-[10px] text-zinc-300 dark:text-zinc-600 mt-0.5">
            Expires {new Date(stamp.expiresAt).toLocaleDateString()}
          </p>
        </div>
        <button
          onClick={onShare}
          className="text-[10px] font-bold text-[#0AC18E] bg-[#0AC18E]/10 px-2.5 py-1.5 rounded-lg border border-[#0AC18E]/20 hover:bg-[#0AC18E]/20 transition-colors shrink-0"
        >
          Share
        </button>
        <button
          onClick={onToggle}
          className={cn(
            'text-[10px] font-bold px-2.5 py-1.5 rounded-lg border transition-colors shrink-0',
            active
              ? 'text-amber-500 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40'
              : 'text-[#0AC18E] bg-[#0AC18E]/10 border-[#0AC18E]/20'
          )}
        >
          {active ? 'Pause' : 'Enable'}
        </button>
        <button
          onClick={() => setShowDelete(true)}
          className="text-[10px] font-bold text-red-400 bg-red-50 dark:bg-red-950/20 px-2.5 py-1.5 rounded-lg border border-red-200 dark:border-red-800/40 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors shrink-0"
        >
          ✕
        </button>
      </div>

      {/* Delete confirmation */}
      {showDelete && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-950/20 border-t border-red-200 dark:border-red-800/40 flex items-center gap-3">
          <p className="text-xs text-red-600 dark:text-red-400 flex-1">Delete this CashStamp?</p>
          <button
            onClick={() => setShowDelete(false)}
            className="text-xs font-semibold text-zinc-500 px-3 py-1.5 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700"
          >
            Cancel
          </button>
          <button
            onClick={onDelete}
            className="text-xs font-bold text-white px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main View ───────────────────────────────────────────────────────────────

export function CashStampsView() {
  const { settings } = usePOS();
  const [stamps, setStamps] = useState(() => loadCashStamps());
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState<Partial<CashStamp> | undefined>(undefined);
  const [tab, setTab] = useState<'active' | 'expired'>('active');
  const [shareResult, setShareResult] = useState<string | null>(null);

  const refresh = () => setStamps(loadCashStamps());

  const stats = useMemo(() => getCashStampStats(stamps), [stamps]);

  const activeStamps = stamps.filter(isStampActive);
  const expiredStamps = stamps.filter((s) => !isStampActive(s));
  const displayedStamps = tab === 'active' ? activeStamps : expiredStamps;

  const handleCreate = (stamp: CashStamp) => {
    upsertCashStamp(stamp);
    refresh();
    setCreating(false);
    setPrefill(undefined);
  };

  const handleToggle = (stamp: CashStamp) => {
    upsertCashStamp({ ...stamp, disabled: !stamp.disabled });
    refresh();
  };

  const handleDelete = (id: string) => {
    deleteCashStamp(id);
    refresh();
  };

  const handleShare = async (stamp: CashStamp) => {
    const url = `${window.location.origin}/stamp/${stamp.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: stamp.title,
          text: `${stamp.merchantName}: ${stamp.title} — Claim free BCH!`,
          url,
        });
        return;
      } catch {
        // Fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareResult(stamp.id);
      setTimeout(() => setShareResult(null), 2500);
    } catch {
      prompt('Copy this link:', url);
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="text-center">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">CashStamps</h2>
        <p className="text-sm text-zinc-400">Promotional BCH giveaways for your customers</p>
      </div>

      {/* Stats overview */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white dark:bg-zinc-800 rounded-2xl px-3 py-3 border border-zinc-100 dark:border-zinc-700 text-center">
          <p className="text-xl font-black text-zinc-900 dark:text-zinc-50">{stats.totalClaims}</p>
          <p className="text-[10px] text-zinc-400 font-semibold uppercase">Total Claims</p>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-2xl px-3 py-3 border border-zinc-100 dark:border-zinc-700 text-center">
          <p className="text-xl font-black text-[#0AC18E]">{formatBCH(stats.totalDistributedBCH)}</p>
          <p className="text-[10px] text-zinc-400 font-semibold uppercase">BCH Sent</p>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-2xl px-3 py-3 border border-zinc-100 dark:border-zinc-700 text-center">
          <p className="text-xl font-black text-zinc-900 dark:text-zinc-50">{stats.totalViews}</p>
          <p className="text-[10px] text-zinc-400 font-semibold uppercase">Views</p>
        </div>
      </div>

      {/* Create button */}
      <button
        onClick={() => { setPrefill(undefined); setCreating(true); }}
        className="w-full py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all flex items-center justify-center gap-2"
      >
        <span className="text-xl">🎁</span>
        Create New CashStamp
      </button>

      {/* Tab switcher */}
      <div className="flex gap-2">
        <button
          onClick={() => setTab('active')}
          className={cn(
            'flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all text-center',
            tab === 'active'
              ? 'bg-[#0AC18E] border-[#0AC18E] text-white'
              : 'bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500'
          )}
        >
          Active ({activeStamps.length})
        </button>
        <button
          onClick={() => setTab('expired')}
          className={cn(
            'flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all text-center',
            tab === 'expired'
              ? 'bg-zinc-600 border-zinc-600 text-white'
              : 'bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500'
          )}
        >
          Ended ({expiredStamps.length})
        </button>
      </div>

      {/* Stamp list */}
      {displayedStamps.length === 0 ? (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-dashed border-zinc-300 dark:border-zinc-600 px-6 py-10 text-center">
          <p className="text-3xl mb-3">{tab === 'active' ? '🎁' : '📦'}</p>
          <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400 max-w-xs mx-auto">
            {tab === 'active'
              ? 'No active CashStamps yet. Create one to start giving BCH to your customers!'
              : 'No ended CashStamps yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedStamps.map((stamp) => (
            <StampCard
              key={stamp.id}
              stamp={stamp}
              onToggle={() => handleToggle(stamp)}
              onShare={() => handleShare(stamp)}
              onDelete={() => handleDelete(stamp.id)}
            />
          ))}
        </div>
      )}

      {/* Share success toast */}
      {shareResult && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-xl z-50">
          ✓ Link copied to clipboard
        </div>
      )}

      {/* How it works — shown when empty */}
      {stamps.length === 0 && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700 space-y-3">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">How CashStamps Work</p>
          <div className="space-y-2.5">
            {[
              { icon: '🎯', text: 'Create a promotion with a BCH budget and reward amount per claim' },
              { icon: '🔗', text: 'Share the unique claim link with customers — in store, on social media, or on receipts' },
              { icon: '💰', text: 'Customers visit the link, see your promotion, and claim their BCH reward' },
              { icon: '📊', text: 'Track claims, views, and BCH distributed in real time' },
            ].map(({ icon, text }, i) => (
              <div key={i} className="flex gap-3 items-start">
                <span className="text-lg shrink-0">{icon}</span>
                <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create form modal */}
      {creating && (
        <CreateStampForm
          initial={prefill}
          merchantName={settings.merchantName}
          onSave={handleCreate}
          onCancel={() => { setCreating(false); setPrefill(undefined); }}
        />
      )}
    </div>
  );
}
